<script setup lang="ts">
import type { TemplateNode, TextAreaVariable } from "./VTextArea.types";
import { computed, onMounted, ref, watch } from "vue";
import { nodesAreEmpty, nodesLength, normalizeNodes, textNode } from "./textAreaNodes";

/**
 * The editable surface of `VTextArea` when it offers variables: text with variable chips, edited in place. Internal to
 * `VTextArea`, which owns the frame, label, toolbar and counter.
 */
defineOptions({
  name: "VTextAreaNodeEditor",
});

const {
  id = undefined,
  variables = [],
  editable = true,
  placeholder = "",
  maxLength = undefined,
  ariaInvalid = undefined,
  ariaDescribedby = undefined,
  ariaLabelledby = undefined,
  ariaBusy = undefined,
} = defineProps<{
  id?: string
  variables?: TextAreaVariable[]
  editable?: boolean
  placeholder?: string
  maxLength?: number
  ariaInvalid?: "true"
  ariaDescribedby?: string
  ariaLabelledby?: string
  ariaBusy?: "true"
}>();

const emit = defineEmits<{
  blur: []
  focus: []
  keydown: [event: KeyboardEvent]
  keyup: [event: KeyboardEvent]
}>();

const model = defineModel<TemplateNode[]>({ default: () => [] });

const editorRef = ref<HTMLDivElement | null>(null);
const focused = ref(false);
/** The nodes the editor itself last produced; an equal model update needs no re-render, which would move the caret. */
let lastEmitted = "";
/** Where the caret was when the editor lost focus, so a toolbar action inserts there instead of at the start. */
let savedRange: Range | null = null;

const empty = computed(() => nodesAreEmpty(model.value));

const CODE_ICON = "<svg viewBox=\"0 0 640 512\" fill=\"currentColor\" aria-hidden=\"true\"><path d=\"M392.8 1.2c-17-4.9-34.7 5-39.6 22l-128 448c-4.9 17 5 34.7 22 39.6s34.7-5 39.6-22l128-448c4.9-17-5-34.7-22-39.6zm80.6 120.1c-12.5 12.5-12.5 32.8 0 45.3L562.7 256l-89.4 89.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l112-112c12.5-12.5 12.5-32.8 0-45.3l-112-112c-12.5-12.5-32.8-12.5-45.3 0zm-306.7 0c-12.5-12.5-32.8-12.5-45.3 0l-112 112c-12.5 12.5-12.5 32.8 0 45.3l112 112c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256l89.4-89.4c12.5-12.5 12.5-32.8 0-45.3z\"/></svg>";
const REMOVE_ICON = "<svg viewBox=\"0 0 384 512\" fill=\"currentColor\" aria-hidden=\"true\"><path d=\"M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z\"/></svg>";

function variableLabel(identifier: string): string {
  return variables.find(variable => variable.identifier === identifier)?.label ?? identifier;
}

function createChip(identifier: string): HTMLSpanElement {
  const chip = document.createElement("span");
  chip.className = "variable-chip";
  chip.contentEditable = "false";
  chip.dataset.identifier = identifier;
  chip.dataset.test = "variable-chip";

  const icon = document.createElement("span");
  icon.className = "chip-icon";
  icon.innerHTML = CODE_ICON;
  chip.appendChild(icon);

  const label = document.createElement("span");
  label.className = "chip-label";
  label.textContent = variableLabel(identifier);
  chip.appendChild(label);

  if (editable) {
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "chip-remove";
    remove.innerHTML = REMOVE_ICON;
    remove.setAttribute("aria-label", label.textContent);
    remove.addEventListener("mousedown", event => event.preventDefault());
    remove.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      chip.remove();
      commit();
    });
    chip.appendChild(remove);
  }

  return chip;
}

/** Reads the editor back into nodes. Line breaks the browser adds as `<br>` or block elements become "\n". */
function readNodes(): TemplateNode[] {
  const nodes: TemplateNode[] = [];
  function append(text: string) {
    nodes.push(textNode(text));
  }

  function walk(parent: Node) {
    parent.childNodes.forEach((child, index) => {
      if (child.nodeType === Node.TEXT_NODE) {
        append(child.textContent ?? "");
        return;
      }
      if (!(child instanceof HTMLElement)) {
        return;
      }
      if (child.classList.contains("variable-chip")) {
        const identifier = child.dataset.identifier;
        if (identifier) {
          nodes.push({ "#type": "AutomationTemplate.Node.Variable", identifier });
        }
        return;
      }
      if (child.tagName === "BR") {
        append("\n");
        return;
      }
      if ((child.tagName === "DIV" || child.tagName === "P") && index > 0) {
        append("\n");
      }
      walk(child);
    });
  }

  if (editorRef.value) {
    walk(editorRef.value);
  }

  return normalizeNodes(nodes);
}

function render() {
  const editor = editorRef.value;
  if (!editor) {
    return;
  }
  editor.replaceChildren(...model.value.map(node => (node["#type"] === "AutomationTemplate.Node.String"
    ? document.createTextNode(node.text)
    : createChip(node.identifier))));
}

function commit() {
  let nodes = readNodes();
  if (maxLength !== undefined && nodesLength(nodes) > maxLength) {
    // Over the limit: keep what was there before this keystroke or paste.
    nodes = model.value;
    render();
  }
  lastEmitted = JSON.stringify(nodes);
  model.value = nodes;
}

function caretInEditor(): Range | null {
  const editor = editorRef.value;
  const selection = window.getSelection();
  if (!editor || !selection || selection.rangeCount === 0 || !editor.contains(selection.anchorNode)) {
    return null;
  }
  return selection.getRangeAt(0);
}

function endOfEditor(): Range | null {
  const editor = editorRef.value;
  if (!editor) {
    return null;
  }
  const range = document.createRange();
  range.selectNodeContents(editor);
  range.collapse(false);
  return range;
}

function insertAtCaret(node: Node, range: Range | null = caretInEditor()) {
  const target = range ?? endOfEditor();
  const selection = window.getSelection();
  if (!target || !selection) {
    return;
  }

  target.deleteContents();
  target.insertNode(node);
  target.setStartAfter(node);
  target.collapse(true);
  selection.removeAllRanges();
  selection.addRange(target);
}

function onBlur() {
  savedRange = caretInEditor()?.cloneRange() ?? null;
  focused.value = false;
  emit("blur");
}

function onKeydown(event: KeyboardEvent) {
  emit("keydown", event);
  if (event.key === "Enter" && !event.defaultPrevented) {
    // A plain newline character keeps the content a list of text and variable nodes.
    event.preventDefault();
    insertAtCaret(document.createTextNode("\n"));
    commit();
  }
}

function onPaste(event: ClipboardEvent) {
  event.preventDefault();
  insertAtCaret(document.createTextNode(event.clipboardData?.getData("text/plain") ?? ""));
  commit();
}

function insertVariable(identifier: string) {
  const editor = editorRef.value;
  if (!editable || !editor) {
    return;
  }
  const range = savedRange && editor.contains(savedRange.startContainer) ? savedRange : endOfEditor();
  editor.focus();
  insertAtCaret(createChip(identifier), range);
  savedRange = null;
  commit();
}

function focus() {
  editorRef.value?.focus();
}

defineExpose({ insertVariable, focus });

watch(model, (nodes) => {
  if (JSON.stringify(nodes) !== lastEmitted) {
    render();
  }
}, { deep: true });

// Chip labels and remove buttons depend on these.
watch(() => [variables, editable], render, { deep: true });

onMounted(render);
</script>

<template>
  <div class="relative">
    <div
      v-if="empty && !focused && placeholder"
      class="pointer-events-none absolute inset-0 text-gray-400 dark:text-dark-400"
      aria-hidden="true"
      data-test="node-editor-placeholder"
    >
      {{ placeholder }}
    </div>
    <div
      :id="id"
      ref="editorRef"
      class="node-editor min-h-[1.5em] whitespace-pre-wrap break-words outline-hidden"
      :contenteditable="editable ? 'true' : 'false'"
      role="textbox"
      aria-multiline="true"
      :aria-placeholder="placeholder || undefined"
      :aria-invalid="ariaInvalid"
      :aria-describedby="ariaDescribedby"
      :aria-labelledby="ariaLabelledby"
      :aria-busy="ariaBusy"
      :aria-disabled="!editable || undefined"
      data-test="node-editor"
      @input="commit"
      @keydown="onKeydown"
      @keyup="emit('keyup', $event)"
      @paste="onPaste"
      @focus="focused = true; emit('focus')"
      @blur="onBlur"
    />
  </div>
</template>

<style lang="scss" scoped>
@reference "../../tailwind-reference.css";

.node-editor {
  :deep(.variable-chip) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 22px;
    padding: 0 8px;
    margin: 0 2px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    user-select: none;
    vertical-align: middle;

    @apply bg-primary-100 text-primary-700;

    :global(.dark) & {
      @apply bg-primary-900/30 text-primary-300;
    }

    .chip-icon,
    .chip-remove {
      display: flex;
      align-items: center;
      flex-shrink: 0;

      svg {
        height: 1em;
      }
    }

    .chip-label {
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 22px;
    }

    .chip-remove {
      padding: 0;
      border: none;
      background: none;
      color: currentColor;
      cursor: pointer;
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }
}
</style>
